/**
 * Hook for handling N-Genius 3DS authentication via the N-Genius WebSDK.
 *
 * The WebSDK mounts a challenge iframe into a DOM element identified by
 * NGENIUS_3DS_CONTAINER_ID and exposes `window.NI.handlePaymentResponse`,
 * which returns a Promise that resolves once 3DS completes. After completion,
 * we poll the Ngenius API (every 3 seconds, up to 10 times) to verify payment status.
 */
import { useCallback } from 'react';
import type { Payment, PaymentOptions } from '../../../core/resources/payments';
import type { PaymentsResource } from '../../../core/resources/payments';
import type { UsePaymentOptions } from '../usePaymentQuery';

export const NGENIUS_3DS_CONTAINER_ID = 'ngenius-3ds-container';

const NGENIUS_SDK_URL_SANDBOX = 'https://paypage.sandbox.ngenius-payments.com/hosted-sessions/sdk.js';
const NGENIUS_SDK_URL_PRODUCTION = 'https://paypage.ngenius-payments.com/hosted-sessions/sdk.js';
const NGENIUS_SDK_SCRIPT_ID = 'ngenius-websdk';

interface UseNgeniusThreedsActionParams {
    paymentsResource: PaymentsResource;
    startPolling: any;
    setError: (error: string | null) => void;
    setIsLoading: (loading: boolean) => void;
    hookOptionsRef: React.MutableRefObject<UsePaymentOptions | undefined>;
}

function ensureNgeniusContainer(): HTMLDivElement {
    let container = document.getElementById(NGENIUS_3DS_CONTAINER_ID) as HTMLDivElement;

    if (!container) {
        container = document.createElement('div');
        container.id = NGENIUS_3DS_CONTAINER_ID;
        container.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: center;
        `;
        document.body.appendChild(container);
        console.log('[N-Genius 3DS] Container created and appended to body');
    }

    return container;
}

function loadNgeniusSdk(isSandboxed: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
        if (document.getElementById(NGENIUS_SDK_SCRIPT_ID)) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.id = NGENIUS_SDK_SCRIPT_ID;
        script.src = isSandboxed ? NGENIUS_SDK_URL_SANDBOX : NGENIUS_SDK_URL_PRODUCTION;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load N-Genius WebSDK'));
        document.head.appendChild(script);
    });
}

export function useNgeniusThreedsAction({
    paymentsResource,
    startPolling,
    setError,
    setIsLoading,
    hookOptionsRef,
}: UseNgeniusThreedsActionParams) {
    const handleNgeniusThreeds = useCallback(
        async (payment: Payment, actionData: any, options: PaymentOptions = {}) => {
            const sdk = actionData.metadata?.sdk as {
                paymentResponse: object;
                orderReference: string;
                paymentReference: string;
                isSandboxed: boolean;
            } | undefined;

            if (!sdk?.paymentResponse || !sdk.orderReference || !sdk.paymentReference) {
                const msg = 'N-Genius 3DS: missing SDK metadata in requireAction';
                console.error(msg, actionData);
                setError(msg);
                setIsLoading(false);
                options.onFailure?.(msg);
                return;
            }

            try {
                // Ensure the container exists in the DOM
                ensureNgeniusContainer();

                await loadNgeniusSdk(sdk.isSandboxed);

                const NI = (window as any).NI;
                if (!NI?.handlePaymentResponse) {
                    throw new Error('N-Genius WebSDK did not expose window.NI.handlePaymentResponse');
                }

                console.log('[N-Genius 3DS] Starting WebSDK challenge flow');

                // Turn off checkout loading overlay - the 3DS modal will be the only overlay
                setIsLoading(false);

                const outcome: { status: string } = await NI.handlePaymentResponse(
                    sdk.paymentResponse,
                    {
                        mountId: NGENIUS_3DS_CONTAINER_ID,
                        style: { width: '100%', height: 500 },
                    },
                );

                console.log('[N-Genius 3DS] WebSDK outcome:', outcome.status);
                console.log('[N-Genius 3DS] 3DS complete - polling Ngenius API for status');

                // Clean up the 3DS container
                const container = document.getElementById(NGENIUS_3DS_CONTAINER_ID);
                if (container) {
                    container.style.display = 'none';
                    container.innerHTML = '';
                    console.log('[N-Genius 3DS] Container hidden and cleared');
                }

                // Turn loading back on while we poll the Ngenius API
                setIsLoading(true);

                // Poll Ngenius API until payment is no longer pending
                const maxAttempts = 10; // 10 attempts
                const pollInterval = 3000; // 3 seconds
                let attempts = 0;

                const pollNgeniusStatus = async (): Promise<void> => {
                    attempts++;
                    console.log(`[N-Genius 3DS] Polling Ngenius API - attempt ${attempts}/${maxAttempts}`);

                    try {
                        const completedPayment = await paymentsResource.ngeniusThreedsComplete({
                            paymentId: payment.id,
                            orderReference: sdk.orderReference,
                            paymentReference: sdk.paymentReference,
                        });

                        console.log('[N-Genius 3DS] Ngenius API response:', {
                            status: completedPayment.status,
                            subStatus: completedPayment.subStatus,
                        });

                        // If payment succeeded
                        if (completedPayment.status === 'succeeded') {
                            setIsLoading(false);
                            const response = { paymentId: completedPayment.id, payment: completedPayment, order: (completedPayment as any).order };
                            await hookOptionsRef.current?.onPaymentCompleted?.(completedPayment, { isRedirectReturn: false, order: response.order });
                            options.onSuccess?.(response);
                            options.onPaymentSuccess?.(response);
                            return;
                        }

                        // If payment failed
                        if (completedPayment.status === 'declined' || completedPayment.status === 'failed') {
                            const errorMsg = (completedPayment as any).error?.message || 'Payment failed';
                            setError(errorMsg);
                            setIsLoading(false);
                            await hookOptionsRef.current?.onPaymentFailed?.(errorMsg, { isRedirectReturn: false });
                            options.onFailure?.(errorMsg);
                            options.onPaymentFailed?.({ code: 'PAYMENT_FAILED', message: errorMsg, payment: completedPayment });
                            return;
                        }

                        // If still pending and we haven't exceeded max attempts, poll again
                        if (attempts < maxAttempts) {
                            setTimeout(() => pollNgeniusStatus(), pollInterval);
                        } else {
                            // Max attempts reached
                            const errorMsg = 'Payment verification timeout - please check your order status';
                            setError(errorMsg);
                            setIsLoading(false);
                            await hookOptionsRef.current?.onPaymentFailed?.(errorMsg, { isRedirectReturn: false });
                            options.onFailure?.(errorMsg);
                            options.onPaymentFailed?.({ code: 'PAYMENT_TIMEOUT', message: errorMsg, payment: completedPayment });
                        }
                    } catch (error) {
                        console.error('[N-Genius 3DS] Error polling Ngenius API:', error);
                        const errorMsg = error instanceof Error ? error.message : 'Failed to verify payment status';
                        setError(errorMsg);
                        setIsLoading(false);
                        await hookOptionsRef.current?.onPaymentFailed?.(errorMsg, { isRedirectReturn: false });
                        options.onFailure?.(errorMsg);
                        options.onPaymentFailed?.({ code: 'NGENIUS_POLL_ERROR', message: errorMsg, payment });
                    }
                };

                // Start polling
                await pollNgeniusStatus();
            } catch (err) {
                const errorMsg = err instanceof Error ? err.message : 'N-Genius 3DS failed';
                console.error('[N-Genius 3DS] Error:', err);

                // Clean up container on error
                const container = document.getElementById(NGENIUS_3DS_CONTAINER_ID);
                if (container) {
                    container.style.display = 'none';
                    container.innerHTML = '';
                }

                setError(errorMsg);
                setIsLoading(false);
                options.onFailure?.(errorMsg);
                options.onPaymentFailed?.({ code: 'NGENIUS_3DS_ERROR', message: errorMsg, payment });
            }
        },
        [paymentsResource, startPolling, setError, setIsLoading, hookOptionsRef],
    );

    return { handleNgeniusThreeds };
}
